import { useState, useEffect, useRef } from 'react';
import { Building2, User, Hash, MapPin, Phone, Mail, Upload, Trash2, Save, CheckCircle, Archive, RotateCcw, Calendar, Sun, Moon, FileText, ScanLine } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useFirmSettings, useUpdateFirmSettings, useUploadFirmTemplate } from '../hooks/useDemandLetters';
import { useArchivedCases, useRestoreCase, useDeleteCase } from '../hooks/useCases';
import { Button, Card, ConfirmModal, EmptyState, IconButton, Input, PageHeader, Skeleton, Tag, TextArea } from '../components/ui';
import ExampleLettersManager from '../components/ExampleLettersManager';
import { extractAxiosDetail } from '../lib/api';
import type { FirmSettings } from '../types';
import type { Case } from '../types';

type FormState = {
  firm_name: string;
  attorney_name: string;
  bar_number: string;
  firm_address: string;
  phone: string;
  email: string;
  statement_engine: 'claude' | 'llamaparse';
};

const EMPTY_FORM: FormState = {
  firm_name: '',
  attorney_name: '',
  bar_number: '',
  firm_address: '',
  phone: '',
  email: '',
  statement_engine: 'claude',
};

function settingsToForm(s: FirmSettings): FormState {
  return {
    firm_name: s.firm_name || '',
    attorney_name: s.attorney_name || '',
    bar_number: s.bar_number || '',
    firm_address: s.firm_address || '',
    phone: s.phone || '',
    email: s.email || '',
    statement_engine: s.statement_engine === 'llamaparse' ? 'llamaparse' : 'claude',
  };
}

export default function FirmSettingsPage() {
  const { data: settings, isLoading } = useFirmSettings();
  const updateMutation = useUpdateFirmSettings();
  const uploadMutation = useUploadFirmTemplate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: archivedCases = [], isLoading: archivedLoading } = useArchivedCases();
  const restoreCase = useRestoreCase();
  const deleteCase = useDeleteCase();
  const [deleteTarget, setDeleteTarget] = useState<Case | null>(null);
  const [confirmClearTemplate, setConfirmClearTemplate] = useState(false);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saved, setSaved] = useState(false);
  const { theme, setTheme } = useTheme();

  const handleRestore = async (publicId: string) => {
    await restoreCase.mutateAsync(publicId);
  };

  const handleDeleteCase = async () => {
    if (!deleteTarget) return;
    await deleteCase.mutateAsync(deleteTarget.public_id);
    setDeleteTarget(null);
  };

  useEffect(() => {
    if (settings) {
      setForm(settingsToForm(settings));
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ ...form });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    }
  };

  const handleTemplateUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadMutation.mutateAsync(file);
    } catch (err) {
      console.error(err);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClearTemplate = async () => {
    try {
      await updateMutation.mutateAsync({ clear_template: true });
    } catch (err) {
      console.error(err);
    }
    setConfirmClearTemplate(false);
  };

  const field = (
    icon: React.ReactNode,
    label: string,
    key: keyof FormState,
    placeholder: string,
    multiline?: boolean,
  ) =>
    multiline ? (
      <TextArea
        label={label}
        rows={3}
        placeholder={placeholder}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        className="resize-none"
      />
    ) : (
      <Input
        label={label}
        type="text"
        leading={icon}
        placeholder={placeholder}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      />
    );

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <PageHeader
        size="title"
        className="mb-8"
        title="Firm Settings"
        description="This information appears on demand letters and other generated documents."
      />

      {isLoading ? (
        <div className="space-y-6">
          <Skeleton variant="rect" className="h-32 rounded-surface" />
          <Skeleton variant="rect" className="h-64 rounded-surface" />
          <Skeleton variant="rect" className="h-40 rounded-surface" />
        </div>
      ) : (
        <div className="space-y-6">
          <Card padding="spacious">
            <h2 className="t-card text-green-900 mb-1">Appearance</h2>
            <p className="t-caption mb-4">
              Choose the look of the sidebar and case header. This is saved on this device only.
            </p>
            <div className="inline-flex rounded-surface border border-border p-1 bg-surface-page">
              <Button
                type="button"
                variant="ghost"
                size="lg"
                icon={Moon}
                onClick={() => setTheme('dark')}
                className={
                  theme === 'dark'
                    ? 'bg-green-800 text-white hover:bg-green-800'
                    : 'text-ink-soft hover:text-ink hover:bg-transparent'
                }
              >
                Dark Green
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="lg"
                icon={Sun}
                onClick={() => setTheme('light')}
                className={
                  theme === 'light'
                    ? 'bg-green-800 text-white hover:bg-green-800'
                    : 'text-ink-soft hover:text-ink hover:bg-transparent'
                }
              >
                Light Cream
              </Button>
            </div>
          </Card>

          <Card padding="spacious" className="space-y-4">
            <h2 className="t-card text-green-900 mb-2">Firm Information</h2>
            {field(<Building2 size={16} />, 'Firm Name', 'firm_name', 'Smith & Jones Law, PLLC')}
            {field(<User size={16} />, 'Attorney Name', 'attorney_name', 'Jane Smith')}
            {field(<Hash size={16} />, 'State Bar Number', 'bar_number', '24012345')}
            {field(<MapPin size={16} />, 'Firm Address', 'firm_address', '123 Main St, Suite 100\nDallas, TX 75201', true)}
            {field(<Phone size={16} />, 'Phone', 'phone', '(214) 555-1234')}
            {field(<Mail size={16} />, 'Email', 'email', 'jsmith@smithjones.com')}
          </Card>

          <Card padding="spacious">
            <h2 className="t-card text-green-900 mb-1">Letterhead Template</h2>
            <p className="t-caption mb-4">
              Upload a .docx or .pdf file to use your firm's letterhead. A .docx letterhead's
              header/footer paragraphs are copied into generated documents; a .pdf letterhead's
              top-of-page text (firm name, address, phone, etc.) is extracted and used as the
              page header on every page. For a true graphical letterhead with logos, upload a
              .docx — its formatting is preserved. Leave blank to use plain formatting.
            </p>
            {settings?.has_template ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-good bg-good-soft rounded-control px-3 py-2 flex-1">
                  <CheckCircle size={16} className="flex-shrink-0" />
                  <span className="truncate">{settings.template_filename || 'Template uploaded'}</span>
                </div>
                <IconButton
                  icon={Trash2}
                  aria-label="Remove template"
                  variant="danger-ghost"
                  onClick={() => setConfirmClearTemplate(true)}
                />
                <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                  Replace
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="lg"
                icon={Upload}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isPending}
                className="border-2 border-dashed border-border font-normal text-ink-faint hover:border-green-600 hover:text-green-700 hover:bg-green-100"
              >
                {uploadMutation.isPending ? 'Uploading…' : 'Upload Letterhead (.docx or .pdf)'}
              </Button>
            )}
            {/* eslint-disable-next-line no-restricted-syntax -- hidden file input; no ui primitive exists for file pickers */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx,.pdf"
              className="hidden"
              onChange={handleTemplateUpload}
            />
            {uploadMutation.isError && (
              <p className="text-danger text-xs mt-2">
                {extractAxiosDetail(uploadMutation.error) ||
                 'Upload failed. Please ensure the file is a valid .docx or .pdf under 10 MB.'}
              </p>
            )}
          </Card>

          <Card padding="spacious">
            <h2 className="t-card text-green-900 mb-1">Statement Reading Engine</h2>
            <p className="t-caption mb-4">
              Choose how uploaded bank and credit-card statements are read. This applies to new uploads across all your cases. You can switch back at any time.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setForm(f => ({ ...f, statement_engine: 'claude' }))}
                className={`h-auto flex-col items-stretch gap-0 whitespace-normal p-4 text-left font-normal ${
                  form.statement_engine === 'claude'
                    ? 'border-green-700 bg-green-100 ring-2 ring-green-700 hover:bg-green-100'
                    : 'border-border hover:bg-cream'
                }`}
              >
                <span className="flex items-center gap-2 mb-1">
                  <FileText size={16} className="text-green-800 flex-shrink-0" />
                  <span className="text-sm font-medium text-ink">Standard (Claude)</span>
                  <Tag tone="good" className="ml-auto">Recommended</Tag>
                </span>
                <span className="text-xs text-ink-faint">
                  The default reader. Fast and accurate for the large majority of statements, including most scans.
                </span>
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setForm(f => ({ ...f, statement_engine: 'llamaparse' }))}
                className={`h-auto flex-col items-stretch gap-0 whitespace-normal p-4 text-left font-normal ${
                  form.statement_engine === 'llamaparse'
                    ? 'border-green-700 bg-green-100 ring-2 ring-green-700 hover:bg-green-100'
                    : 'border-border hover:bg-cream'
                }`}
              >
                <span className="flex items-center gap-2 mb-1">
                  <ScanLine size={16} className="text-green-800 flex-shrink-0" />
                  <span className="text-sm font-medium text-ink">High-accuracy OCR (LlamaParse)</span>
                </span>
                <span className="text-xs text-ink-faint">
                  A heavier reader for difficult scanned or image-only statements. Slower and costs more per page.
                </span>
              </Button>
            </div>
          </Card>

          <ExampleLettersManager />

          <div className="flex justify-end">
            <Button
              size="lg"
              icon={saved ? CheckCircle : Save}
              loading={updateMutation.isPending}
              onClick={handleSave}
            >
              {saved ? 'Saved' : updateMutation.isPending ? 'Saving…' : 'Save Settings'}
            </Button>
          </div>

          <Card padding="spacious">
            <h2 className="t-card text-green-900 mb-1 flex items-center gap-2">
              <Archive size={16} className="text-ink-faint" />
              Archived Cases
            </h2>
            <p className="t-caption mb-4">
              Restore or permanently delete cases you have moved to the trash.
            </p>
            {archivedLoading ? (
              <div className="space-y-2 py-2">
                <Skeleton variant="rect" className="h-16 rounded-control" />
                <Skeleton variant="rect" className="h-16 rounded-control" />
              </div>
            ) : archivedCases.length === 0 ? (
              <div className="border border-dashed border-border rounded-control">
                <EmptyState
                  size="inline"
                  icon={Trash2}
                  title="No archived cases"
                  description="Cases you archive will appear here."
                />
              </div>
            ) : (
              <div className="space-y-2">
                {archivedCases.map((c: Case) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between border border-border rounded-control px-4 py-3"
                  >
                    <div>
                      <h3 className="text-sm font-medium text-ink">{c.name}</h3>
                      {c.client_name && (
                        <p className="text-xs text-ink-faint">{c.client_name}</p>
                      )}
                      {c.archived_at && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-ink-faint">
                          <Calendar size={12} />
                          Archived {new Date(c.archived_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={RotateCcw}
                        onClick={() => handleRestore(c.public_id)}
                        disabled={restoreCase.isPending}
                      >
                        Restore
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        icon={Trash2}
                        onClick={() => setDeleteTarget(c)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      <ConfirmModal
        open={confirmClearTemplate}
        onClose={() => setConfirmClearTemplate(false)}
        onConfirm={handleClearTemplate}
        title="Remove Letterhead Template"
        message="The uploaded letterhead template will be removed and generated documents will use plain formatting."
        confirmLabel="Remove Template"
        variant="danger"
        loading={updateMutation.isPending}
      />

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteCase}
        title="Permanently Delete Case"
        message={`Are you sure you want to permanently delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete Forever"
        variant="danger"
        loading={deleteCase.isPending}
      />
    </div>
  );
}
